import '../style/global.css';

import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import Box from "@mui/material/Box";
import { DataGrid } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid/models/colDef';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { IUserDialogPromptReportRow, ReportApi } from '../api/ReportApi';
import background from '../asset/background-login.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

// npm install @mui/x-data-grid --legacy-peer-deps
// npm install @mui/x-data-grid@5.13.1

const columns: GridColDef[] = [
    { field: 'user_id', headerName: 'User ID', width: 90 },
    { field: 'user_first_name', headerName: 'First Name', width: 150 },
    { field: 'user_last_name', headerName: 'Last Name', width: 150 },
    { field: 'user_email', headerName: 'Email', width: 200 },
    { field: 'course_name', headerName: 'Course', width: 180 },
    { field: 'lesson_name', headerName: 'Lesson', width: 180 },
    { field: 'dialog_title', headerName: 'Dialog', width: 180 },
    { field: 'dialog_prompt_count', headerName: 'Prompt Complete', type: 'number', width: 130 },
    { field: 'total_dialog_prompt_count', headerName: 'Total Prompt', type: 'number', width: 130 },
    {
        field: 'user_dialog_prompt_datetime',
        headerName: 'Timestamp',
        type: 'dateTime', // enables proper sorting and formatting
        width: 240,
        valueFormatter: (value: Date | string | null) =>
            value ? new Date(value).toLocaleString() : '',
    }

];

export default function UserDialogPromptReport() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.UserDialogPromptReport)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [reportRowList, setReportRowList] = React.useState<IUserDialogPromptReportRow[]>([]);
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });


    const [emailFilter, setEmailFilter] = React.useState('');
    const [courseFilter, setCourseFilter] = React.useState('');
    const [lessonFilter, setLessonFilter] = React.useState('');
    const [dialogFilter, setDialogFilter] = React.useState('');


    React.useEffect(() => {
        ReportApi.getUserDialogPrompt()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to CourseApi.getAll() " + response.status)
            })
            .then(list => {
                setReportRowList(list);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setErrorMessage]);

    const filteredRows = reportRowList
        .filter((row) =>
            emailFilter ? row.user_email.toLowerCase().includes(emailFilter.toLowerCase()) : true
        )
        .filter((row) =>
            courseFilter ? row.course_name === courseFilter : true
        )
        .filter((row) =>
            lessonFilter ? row.lesson_name === lessonFilter : true
        )
        .filter((row) =>
            dialogFilter ? row.dialog_title === dialogFilter : true
        )
        .map((row) => ({
            ...row,
            id: `${row.user_id}-${row.dialog_id}`,
            user_dialog_prompt_datetime: new Date(row.user_dialog_prompt_datetime), // Convert to Date object
        }));

    return (
        <div style={{
            display: 'flex', // Use flex to allow easy centering
            flexDirection: 'column', // Changed to stack children vertically
            justifyContent: 'center', // Center horizontally
            alignItems: 'center', // Center vertically
            // minHeight: 'calc(100vh - 85px)', // Ensure the div takes at least the full viewport height
            width: '100%', // Ensure the div takes the full viewport width
            padding: 0, // Reset any padding
            backgroundImage: `url(${background})`, // Your background image
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover', // Cover the entire size of the div
            backgroundPosition: 'center', // Center the background image
        }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '90%' }}>
                <TextField
                    label="Email"
                    value={emailFilter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailFilter(e.target.value)}
                    fullWidth
                />
                <FormControl fullWidth>
                    <InputLabel id="user-dialog-prompt-report-course-label">Course</InputLabel>
                    <Select
                        labelId="user-dialog-prompt-report-course-label"
                        id="user-dialog-prompt-report-course"
                        value={courseFilter}
                        label="Course"
                        onChange={(e) => setCourseFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Array.from(new Set(reportRowList.map(r => r.course_name))).map(name => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel id="user-dialog-prompt-report-lesson-label">Lesson</InputLabel>
                    <Select
                        labelId="user-dialog-prompt-report-lesson-label"
                        id="user-dialog-prompt-report-lesson"
                        value={lessonFilter}
                        label="Lesson"
                        onChange={(e) => setLessonFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Array.from(new Set(reportRowList.map(r => r.lesson_name))).map(name => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel id="user-dialog-prompt-report-dialog-label">Dialog</InputLabel>
                    <Select
                        labelId="user-dialog-prompt-report-dialog-label"
                        id="user-dialog-prompt-report-dialog"
                        value={dialogFilter}
                        label="Dialog"
                        onChange={(e) => setDialogFilter(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {Array.from(new Set(reportRowList.map(r => r.dialog_title))).map(name => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box
                sx={{
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // maxHeight: 'calc(100vh - 300px)', // Viewport height minus 100px
                    width: '90%', // Set the width of the Box
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    overflowY: 'auto', // Allows content to scroll if it overflows the Y axis
                    border: '1.5px solid rgba(82, 99, 125, 0.2)', // Added border with color and transparency
                    padding: 4,
                    position: 'relative', // Required for absolute children to anchor
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    autoHeight
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    sx={{ width: '100%' }}
                />
            </Box>
        </div>
    );
}
